<script setup lang="ts">
import { SolRadio } from '@solfacil/girassol'

interface Props {
  title: string
  price: string
  economy: string
  points: string
  storeInfo: string
  linkText?: string
  showBestOffer?: boolean
  selected: boolean
  disabled: boolean
  hasLink?: boolean
  hideSelection?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showBestOffer: false,
  linkText: '',
  hasLink: true,
  hideSelection: false,
})

const emit = defineEmits<{
  select: []
  linkClick: []
  conecteModalClick: ['benefits' | 'inverters']
}>()

const { formatMoney } = useMoney()

function handleClick() {
  if (!props.disabled) {
    emit('select')
  }
}

function handleLinkClick() {
  emit('linkClick')
}

function handleConecteModalClick() {
  emit('conecteModalClick', 'inverters')
}
</script>

<template>
  <div
    class="installment-card"
    :class="{
      'card-selected': selected,
      'card-disabled': disabled,
    }"
    @click="handleClick"
  >
    <!-- Badge "Melhor oferta" -->
    <SolTag
      v-if="showBestOffer"
      id="best-offer-tag"
      variant="fill"
      class="card-badge-best"
    >
      Melhor oferta
    </SolTag>

    <!-- Header com Radio -->
    <div class="card-header">
      <SolRadio
        v-if="!hideSelection"
        :id="`installment-radio-${title.replace(/\s+/g, '-').toLowerCase()}`"
        :checked="selected"
        :disabled="disabled"
        :hide-label="true"
        :label="title"
        :value="title"
        name="installment-selection"
        @change="handleClick"
      />

      <!-- Title -->
      <h3 class="card-title">
        {{ title }}
      </h3>
    </div>

    <!-- Price -->
    <div class="card-section">
      <span class="card-label">Parcela</span>
      <span class="card-money-value">{{ price }}</span>
    </div>

    <!-- Economy -->
    <div v-if="!hideSelection" class="card-section">
      <span class="card-label">Seu cliente economiza</span>
      <span
        class="card-economy-value"
        :class="{ 'card-economy-value-zero': economy === formatMoney(0) }"
      >
        {{ economy }}
      </span>
    </div>

    <!-- Points -->
    <!-- <div class="card-section">
      <span class="card-label">Solfácil Mais</span>
      <span class="card-points-value">{{ points }}</span>
    </div> -->

    <!-- Store Info -->
    <div class="card-section">
      <span class="card-label">Compra do Kit</span>
      <span class="card-store-value">{{ storeInfo }}</span>
      <div v-if="hideSelection" class="flex gap-1">
        <span class="flex card-store-value">Requer </span>
        <SolLink
          id="show-combo-conecte-inverters"
          target="_blank"
          class="card-store-value card-link"
          @click.stop="handleConecteModalClick"
        >
          inversores compatíveis
        </SolLink>
      </div>
    </div>

    <!-- Link -->
    <a v-if="hasLink && linkText" href="#" class="card-link" @click.prevent.stop="handleLinkClick">
      {{ linkText }}
    </a>
  </div>
</template>

<style lang="scss" scoped>
.installment-card {
  @apply relative flex flex-col items-center bg-neutral-high-pure cursor-pointer;
  @apply p-4xs pb-[28px] gap-4 min-w-[188px] flex-shrink-0 flex-1;
  @apply transition-all duration-200;
  @apply border border-neutral-high-medium rounded-lg;
  scroll-snap-align: start;

  &:hover:not(.card-disabled) {
    @apply border-brand-secondary-pure;
    box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.08);
  }

  &.card-selected {
    @apply border-brand-secondary-pure;
  }

  &.card-disabled {
    @apply opacity-50 cursor-not-allowed;
  }

  .card-badge-best {
    @apply absolute top-[-11px] left-1/2 whitespace-nowrap;
    @apply bg-brand-secondary-medium fonts-body-small-regular;
    transform: translateX(-50%);
  }

  .card-header {
    @apply flex flex-col items-center gap-2;

    .card-title {
      @apply fonts-body-medium-bold text-neutral-low-dark whitespace-nowrap;
    }
  }

  .card-section {
    @apply flex flex-col items-center justify-center;

    .card-label {
      @apply fonts-body-small-regular text-neutral-low-light whitespace-nowrap;
    }

    .card-money-value {
      @apply fonts-body-medium-regular text-neutral-low-dark whitespace-nowrap;
    }

    .card-economy-value {
      @apply fonts-body-small-bold text-brand-secondary-pure whitespace-nowrap;

      &.card-economy-value-zero {
        @apply text-feedback-negative-pure;
      }
    }

    .card-points-value {
      @apply fonts-body-small-bold text-brand-secondary-pure whitespace-nowrap;
    }

    .card-store-value {
      @apply fonts-body-small-bold text-neutral-low-light whitespace-nowrap;
    }
  }

  .card-link {
    @apply flex items-center justify-center w-full gap-1 min-w-0;
    @apply fonts-body-small-regular text-neutral-low-light text-center;
    @apply underline cursor-pointer break-words;

    &:hover {
      @apply text-brand-secondary-pure;
    }
  }
}
</style>
